import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icons";
import { Avatar } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/ui/status-badges";
import logoWhite from "@/assets/svgs/Logo white.svg";
import type { Tenant } from "@/types";

const NAV = [
  { label: "Dashboard", path: "/", icon: "dashboard" },
  { label: "Tenants", path: "/tenants", icon: "building" },
];

/* ---------- Sidebar ---------- */
export function Sidebar({
  onLogout,
  mobileOpen,
  setMobileOpen,
}: {
  onLogout: () => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
}) {
  const location = useLocation();
  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={cn(
          "flex h-full shrink-0 flex-col bg-gray1 text-white transition-all duration-200 w-60",
          mobileOpen ? "fixed inset-y-0 left-0 z-[60]" : "hidden lg:flex",
        )}
      >
        <div className="flex h-14 items-center px-4 justify-between border-b border-gray2">
          <img src={logoWhite} alt="Teelogix" className="h-7" />
          <button
            className="lg:hidden text-gray4"
            onClick={() => setMobileOpen(false)}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <nav className="flex-1 space-y-1 py-3 px-3">
          {NAV.map((item) => {
            const active =
              item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "group relative flex items-center rounded-lg text-[13px] font-semibold transition-colors w-full gap-3 px-3 py-2.5",
                  active
                    ? "bg-buttonColor text-white shadow-[0_2px_8px_rgba(26,139,90,.4)]"
                    : "text-gray4 hover:bg-gray2 hover:text-white",
                )}
              >
                <Icon name={item.icon} size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="border-t border-gray2 py-3 px-3">
          <button
            onClick={onLogout}
            className="flex items-center rounded-lg text-[13px] font-semibold text-gray4 hover:bg-gray2 hover:text-white transition-colors w-full gap-3 px-3 py-2.5"
          >
            <Icon name="logout" size={18} />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}

/* ---------- Notifications ---------- */
export function NotificationsButton() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Icon name="bell" size={18} />
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-[13px] font-bold text-foreground">
              Notifications
            </span>
          </div>
          <div className="max-h-80 overflow-y-auto px-4 py-3">
            <p className="text-[12px] text-muted-foreground text-center py-4">
              No new notifications
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Topbar ---------- */
export function Topbar({
  crumbs,
  onMenu,
  onOpenCmd,
  user,
}: {
  crumbs: Array<{ label: string; onClick?: () => void }>;
  onMenu: () => void;
  onOpenCmd: () => void;
  user: { name: string; role: string };
}) {
  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-border bg-card/80 px-4 backdrop-blur-md lg:px-6">
      <button className="lg:hidden text-muted-foreground" onClick={onMenu}>
        <Icon name="menu" size={20} />
      </button>
      <nav className="flex items-center gap-1.5 text-[13px] min-w-0">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && (
              <Icon
                name="chevronRight"
                size={14}
                className="text-muted-foreground/50 shrink-0"
              />
            )}
            {c.onClick ? (
              <button
                onClick={c.onClick}
                className="shrink-0 whitespace-nowrap font-medium text-muted-foreground hover:text-foreground"
              >
                {c.label}
              </button>
            ) : (
              <span className="max-w-[42vw] truncate whitespace-nowrap font-bold text-foreground sm:max-w-none">
                {c.label}
              </span>
            )}
          </span>
        ))}
      </nav>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          onClick={onOpenCmd}
          className="hidden md:flex h-9 items-center gap-2 rounded-md border border-border bg-muted/50 pl-2.5 pr-2 text-[12px] text-muted-foreground hover:bg-muted"
        >
          <Icon name="search" size={15} />
          <span className="font-medium">Search…</span>
          <kbd className="ml-2 flex items-center gap-0.5 rounded border border-border bg-card px-1.5 py-0.5 text-[10px] font-semibold">
            ⌘K
          </kbd>
        </button>
        <button
          onClick={onOpenCmd}
          className="md:hidden flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <Icon name="search" size={18} />
        </button>
        <NotificationsButton />
        <div className="ml-1 flex items-center gap-2 rounded-full pl-1 pr-2.5 py-1 hover:bg-muted cursor-default">
          <Avatar name={user.name} size={28} />
          <div className="hidden sm:block leading-tight">
            <div className="whitespace-nowrap text-[12px] font-semibold text-foreground">
              {user.name}
            </div>
            <div className="whitespace-nowrap text-[10px] text-muted-foreground">
              {user.role}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ---------- Command Palette ---------- */
export function CommandPalette({
  open,
  onClose,
  tenants,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  tenants: Tenant[];
  onNavigate: (path: string) => void;
}) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const actions = [
    {
      id: "nav-dash",
      label: "Go to Dashboard",
      icon: "dashboard",
      kind: "Navigate",
      run: () => onNavigate("/"),
    },
    {
      id: "nav-tenants",
      label: "Go to Tenants",
      icon: "building",
      kind: "Navigate",
      run: () => onNavigate("/tenants"),
    },
    {
      id: "act-new",
      label: "Create new tenant",
      icon: "plus",
      kind: "Action",
      run: () => onNavigate("/tenants?new=1"),
    },
  ];

  const ql = q.toLowerCase();
  const tHits = q
    ? tenants
        .filter((t) => t.name.toLowerCase().includes(ql) || t.slug.includes(ql))
        .slice(0, 6)
    : tenants.slice(0, 4);
  const aHits = actions.filter((a) => !q || a.label.toLowerCase().includes(ql));
  const flat = [
    ...aHits.map((a) => ({ ...a, group: "action" as const })),
    ...tHits.map((t) => ({
      id: t.id,
      label: t.name,
      sub: t.slug,
      icon: "building",
      group: "tenant" as const,
      run: () => onNavigate(`/tenants/${t.id}`),
    })),
  ];

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowDown") {
        e.preventDefault();
        setIdx((i) => Math.min(i + 1, flat.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        flat[idx]?.run();
        onClose();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, flat, idx, onClose]);

  if (!open) return null;

  let running = -1;
  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 pt-[12vh]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-3 border-b border-border px-4">
          <Icon name="search" size={18} className="text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setIdx(0);
            }}
            placeholder="Search tenants or run a command…"
            className="h-12 flex-1 bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
            esc
          </kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {aHits.length > 0 && (
            <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              Actions
            </div>
          )}
          {aHits.map((a) => {
            running++;
            const cur = running;
            return (
              <CmdRow
                key={a.id}
                active={idx === cur}
                onMouseEnter={() => setIdx(cur)}
                onClick={() => {
                  a.run();
                  onClose();
                }}
                icon={a.icon}
                label={a.label}
                tag={a.kind}
              />
            );
          })}
          <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            Tenants
          </div>
          {tHits.length === 0 && (
            <div className="px-3 py-4 text-center text-[12px] text-muted-foreground">
              No tenants match "{q}".
            </div>
          )}
          {tHits.map((t) => {
            running++;
            const cur = running;
            return (
              <CmdRow
                key={t.id}
                active={idx === cur}
                onMouseEnter={() => setIdx(cur)}
                onClick={() => {
                  onNavigate(`/tenants/${t.id}`);
                  onClose();
                }}
                icon="building"
                label={t.name}
                sub={t.slug}
                right={<StatusBadge status={t.status} withDot={false} />}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CmdRow({
  active,
  onClick,
  onMouseEnter,
  icon,
  label,
  sub,
  tag,
  right,
}: {
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  icon: string;
  label: string;
  sub?: string;
  tag?: string;
  right?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left",
        active ? "bg-primary/10" : "",
      )}
    >
      <span
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-md",
          active
            ? "bg-primary/15 text-primary"
            : "bg-muted text-muted-foreground",
        )}
      >
        <Icon name={icon} size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-foreground">
          {label}
        </span>
        {sub && (
          <span className="block truncate text-[11px] text-muted-foreground">
            {sub}
          </span>
        )}
      </span>
      {tag && (
        <span className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {tag}
        </span>
      )}
      {right}
    </button>
  );
}
